import * as React from 'react';
import getPackageBaseUrl from 'owa-session-store/lib/actions/getPackageBaseUrl';
import RibbonButton, { RibbonButtonState } from './buttons/RibbonButton';
import RibbonFormatState from '../store/schema/RibbonFormatState';
import { format } from 'owa-strings';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { Image } from 'office-ui-fabric-react/lib/Image';
import { isBrowserIE } from 'owa-user-agent/lib/userAgent';
import { isMac } from 'owa-user-agent/lib/userAgent';
import { observer } from 'mobx-react';
import { resolve as urlResolve } from 'url';

let styles = require('./Ribbon.scss');
let classNames = require('classnames/bind').bind(styles);
let mouseDownButtonKey: string;

// According to https://www.w3schools.com/jsref/event_detail.asp
// MouseEvent has a detail property to find out how many times the mouse was clicked in the same area.
interface MouseEvent extends React.MouseEvent<EventTarget> {
    detail: number;
}

export type RibbonButtonViewProps = {
    ribbonButton: RibbonButton;
    formatViewState: RibbonFormatState;
    isDropDownOpen: boolean;
    containerRef: React.Ref<HTMLDivElement>;
    onClick: () => void;
    isRtl: boolean;
};

const RibbonButtonView = (props: RibbonButtonViewProps): JSX.Element => {
    const { ribbonButton, formatViewState, isDropDownOpen, onClick, containerRef, isRtl } = props;
    const {
        key,
        buttonState,
        title,
        shortcutMac,
        shortcutWin,
        createDropDownCallback,
        imageRtlSrc,
        imageSrc,
    } = ribbonButton;
    const buttonStateValue = buttonState ? buttonState(formatViewState) : RibbonButtonState.Normal;
    const isDisabled = buttonStateValue == RibbonButtonState.Disabled;
    const buttonClassName = classNames(
        styles.ribbonButton,
        (isDropDownOpen || buttonStateValue == RibbonButtonState.Checked) && styles.isChecked,
        isDisabled && styles.isDisabled
    );
    const titleStr = format(title(), (isMac() ? shortcutMac : shortcutWin) || '');
    const hasPopUp = createDropDownCallback != null;
    return (
        <div className={styles.ribbonButtonWrapper} ref={containerRef} key={key}>
            <IconButton
                className={buttonClassName}
                data-is-focusable={true}
                title={titleStr}
                onClick={isDisabled ? null : e => handleClickEvent(key, e, onClick)}
                onMouseDown={isDisabled ? null : e => handleMouseDownEvent(key, e)}
                onMouseUp={isDisabled ? null : e => handleMouseUpEvent(key, e, onClick)}
                ariaLabel={titleStr}
                disabled={isDisabled}
                checked={buttonState && buttonStateValue == RibbonButtonState.Checked}
                aria-haspopup={hasPopUp}
                aria-expanded={hasPopUp ? isDropDownOpen : null}>
                <Image
                    className={styles.image}
                    shouldFadeIn={false}
                    src={urlResolve(
                        getPackageBaseUrl(),
                        isRtl && imageRtlSrc ? imageRtlSrc : imageSrc
                    )}
                />
            </IconButton>
        </div>
    );
};

function handleMouseDownEvent(key: string, e: React.MouseEvent<EventTarget>) {
    // Remember the key user clicked so that we can check whether mouse is on the same button when mouse up
    // check for left button only (button == 0)
    if (e.button == 0) {
        // Prevent default so that the button won't get focused, editor won't lose focus.
        // Otherwise editor will lose current temporary state (B/I/U/...)
        e.preventDefault();
        mouseDownButtonKey = key;
    }
}

function handleMouseUpEvent(key: string, e: React.MouseEvent<EventTarget>, onClick: () => void) {
    // If left mouse up from the same button when mouse down, treat it as a click event
    if (e.button == 0 && mouseDownButtonKey == key) {
        onClick();
    }
    mouseDownButtonKey = null;
}

function handleClickEvent(key: string, e: React.MouseEvent<EventTarget>, onClick: () => void) {
    // This can be triggered from mouse click or keyboard hit.
    // For mouse click, since we just handled the event in mouseUp handler, we need to ignore it.
    // For keyboard hit, we should still handle it.
    // To check whether this is from keyboard:
    // In browsers other than IE, e.detail will return 0 here which means we didn't really click mouse
    // In IE, e.detail always return 0 (IE bug?), so we check nativeEvent.x instead and it will be a negative number
    if (
        (isBrowserIE() && e.nativeEvent.x < 0) ||
        (!isBrowserIE() && (e as MouseEvent).detail == 0)
    ) {
        onClick();
    }
}

export default observer(RibbonButtonView);
